import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ShieldCheck, AlertTriangle, XCircle, Download, FileText, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const STATUS_STYLE = {
  PASSING: { color: "#0CDC73", label: "Passing", Icon: ShieldCheck },
  WARNING: { color: "#F6AD55", label: "Warning", Icon: AlertTriangle },
  FAILING: { color: "#E53E3E", label: "Failing", Icon: XCircle },
};

const ROLE_LEVELS = {
  CANDIDATE: 0, HIRING_MANAGER: 1, RECRUITER: 2, HR_ADMIN: 3, SUPER_ADMIN: 4, PLATFORM_OWNER: 5,
};

export default function Compliance() {
  const { user } = useAuth();
  const canExport = (ROLE_LEVELS[user?.role] ?? 0) >= ROLE_LEVELS.HR_ADMIN;
  const [h, setH] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/compliance/health").then(r => setH(r.data)).catch(() => {});
    if (canExport) api.get("/compliance/evidence-packs").then(r => setHistory(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportPack = async () => {
    setBusy(true);
    try {
      const resp = await fetch(`${API}/compliance/evidence-pack`, { credentials: "include" });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const packId = resp.headers.get("x-pack-id") || "pack";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `alfred-evidence-${packId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Evidence pack downloaded");
      load();
    } catch (e) { toast.error("Export failed"); }
    finally { setBusy(false); }
  };

  if (!h) return <div>Loading…</div>;

  return (
    <div className="space-y-6" data-testid="compliance-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">Compliance</div>
          <h1 className="text-3xl font-bold mt-1">Health Score</h1>
          <p className="text-neutral-600 mt-1">Continuous monitoring against {h.framework}.</p>
        </div>
        {canExport && (
          <Button className="btn-primary rounded-full" onClick={exportPack} disabled={busy} data-testid="export-evidence-btn">
            <Download className="w-4 h-4 mr-1" /> {busy ? "Generating…" : "Export evidence pack"}
          </Button>
        )}
      </div>

      {/* Evidence pack banner */}
      {canExport && (
        <Card className="border-0 shadow-sm relative overflow-hidden" data-testid="evidence-pack-banner">
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(255,0,127,0.06) 0%, rgba(12,220,115,0.06) 100%)" }} />
          <CardContent className="p-6 relative">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-lg bg-white grid place-items-center shadow-sm">
                  <FileText className="w-5 h-5 text-pink" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">Audit-ready evidence, one click away.</h3>
                    <Badge className="bg-teal text-white border-0 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Vanta-style</Badge>
                  </div>
                  <p className="text-sm text-neutral-600 mt-1">
                    Generate a signed PDF pack containing monitor states, e-signature evidence, certification wallet snapshots and the recent audit trail — everything an ISO 27001 / ISO 42001 / SOC 2 auditor asks for.
                  </p>
                </div>
              </div>
              <Button className="btn-primary rounded-full" onClick={exportPack} disabled={busy} data-testid="export-evidence-btn-2">
                <Download className="w-4 h-4 mr-1" /> Generate pack
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="border-0 shadow-sm lg:col-span-1">
          <CardContent className="p-6 text-center">
            <div className="relative inline-block">
              <svg width="180" height="180" viewBox="0 0 180 180">
                <circle cx="90" cy="90" r="80" stroke="#eee" strokeWidth="12" fill="none" />
                <circle cx="90" cy="90" r="80" stroke="#FF007F" strokeWidth="12" fill="none"
                  strokeDasharray={`${(h.score / 100) * 502.65} 502.65`} strokeLinecap="round"
                  transform="rotate(-90 90 90)" />
              </svg>
              <div className="absolute inset-0 grid place-items-center">
                <div>
                  <div className="text-5xl font-bold text-pink">{h.score}</div>
                  <div className="text-neutral-500 text-sm">/ 100</div>
                </div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2">
              <div><div className="text-xl font-bold text-teal">{h.counts.passing}</div><div className="text-[10px] uppercase text-neutral-500">Passing</div></div>
              <div><div className="text-xl font-bold" style={{color: "#F6AD55"}}>{h.counts.warning}</div><div className="text-[10px] uppercase text-neutral-500">Warning</div></div>
              <div><div className="text-xl font-bold text-red-500">{h.counts.failing}</div><div className="text-[10px] uppercase text-neutral-500">Failing</div></div>
            </div>

            {h.by_framework && Object.keys(h.by_framework).length > 0 && (
              <div className="mt-6 pt-6 border-t space-y-3 text-left" data-testid="framework-breakdown">
                <div className="text-xs uppercase tracking-widest text-neutral-500">By framework</div>
                {Object.entries(h.by_framework).map(([f, b]) => (
                  <div key={f} data-testid={`framework-${f.replace(/\s+/g,'-')}`}>
                    <div className="flex justify-between items-baseline">
                      <span className="text-sm font-semibold">{f}</span>
                      <span className="text-xs font-mono text-pink">{b.score}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden mt-1">
                      <div className="h-full bg-pink" style={{ width: `${b.score}%`, transition: "width 400ms" }} />
                    </div>
                    <div className="text-[10px] text-neutral-500 mt-1">{b.passing}/{b.total} passing · {b.warning} warning · {b.failing} failing</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm lg:col-span-2">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4">All monitors</h3>
            <div className="space-y-2">
              {h.monitors.map(m => {
                const S = STATUS_STYLE[m.status];
                return (
                  <div key={m.id} className="flex items-center justify-between p-3 rounded-lg border" data-testid={`monitor-${m.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <S.Icon className="w-4 h-4 shrink-0" style={{ color: S.color }} />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{m.name}</div>
                        <div className="text-xs text-neutral-500 flex flex-wrap items-center gap-2">
                          <span>Target: {m.target}</span>
                          {m.framework && <Badge variant="outline" className="text-[9px]">{m.framework}</Badge>}
                          <span>Checked {new Date(m.last_checked).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                    <Badge style={{ backgroundColor: S.color, color: "white", border: 0 }}>{S.label}</Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {canExport && (
        <Card className="border-0 shadow-sm" data-testid="evidence-history-card">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4">Recent evidence packs</h3>
            {history.length === 0 ? (
              <div className="text-sm text-neutral-500">No packs generated yet. Click "Export evidence pack" above.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-neutral-500 tracking-wider">
                  <tr><th className="pb-2">Pack ID</th><th className="pb-2">Generated by</th><th className="pb-2">Score</th><th className="pb-2">Monitors</th><th className="pb-2">Signatures</th><th className="pb-2">Certs</th><th className="pb-2">When</th></tr>
                </thead>
                <tbody>
                  {history.map(p => (
                    <tr key={p.pack_id} className="border-t" data-testid={`pack-${p.pack_id}`}>
                      <td className="py-2 font-mono text-xs">{p.pack_id}</td>
                      <td className="py-2">{p.generated_by}</td>
                      <td className="py-2 font-bold text-pink">{p.score}</td>
                      <td className="py-2">{p.monitor_count}</td>
                      <td className="py-2">{p.signature_count}</td>
                      <td className="py-2">{p.certification_count}</td>
                      <td className="py-2 text-neutral-500 text-xs">{new Date(p.generated_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
